import React, { useEffect, useMemo, useState } from "react";
import OfferMarquee from "../../components/OfferMarquee";
import HeroSection from "../../components/home/HeroSection";
import ProductRailSection from "../../components/home/ProductRailSection";
import CategorySection from "../../components/home/CategorySection";
import CustomDesignSection from "../../components/home/CustomDesignSection";
import PromoBanner from "../../components/home/PromoBanner";
import TestimonialSection from "../../components/home/TestimonialSection";
import InstagramGallery from "../../components/home/InstagramGallery";
import TrustBadges from "../../components/TrustBadges";
import BrandStorySection from "../../components/home/BrandStorySection";
import { listBestsellers, listProducts } from "../../services/productService";
import { CATEGORY_IMAGES, GALLERY_IMGS, HERO_IMG, REVIEWS } from "./homeData";
import { setPageMeta } from "../../utils/seo";

const HomePage = () => {
  const [homeProducts, setHomeProducts] = useState({
    bestSellers: [],
    trending: [],
    newArrivals: [],
  });
  const [loadingProducts, setLoadingProducts] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoadingProducts(true);

    Promise.allSettled([
      listBestsellers(),
      listProducts({ sort: "best_selling" }),
      listProducts({ sort: "newest" }),
    ])
      .then(([bestsellerResult, trendingResult, newestResult]) => {
        if (cancelled) return;
        const bestSellers = bestsellerResult.status === "fulfilled" ? bestsellerResult.value : [];
        const trending = trendingResult.status === "fulfilled" ? trendingResult.value : [];
        const newArrivals = newestResult.status === "fulfilled" ? newestResult.value : [];

        setHomeProducts({
          bestSellers: bestSellers.length ? bestSellers : trending,
          trending: trending.length ? trending : bestSellers,
          newArrivals,
        });
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoadingProducts(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setPageMeta({
      title: "Cynos | Premium Oversized T-Shirts & Streetwear India",
      description: "Shop premium oversized T-shirts, anime tees, graphic tees, and streetwear styles for Indian youth at Cynos.",
      canonical: window.location.origin,
      og: {
        type: "website",
        title: "Cynos | Premium Oversized T-Shirts & Streetwear India",
        description: "Shop premium oversized T-shirts, anime tees, graphic tees, and streetwear styles for Indian youth at Cynos.",
        image: HERO_IMG,
        url: window.location.origin,
        site_name: "Cynos",
      },
      twitter: {
        card: "summary_large_image",
        title: "Cynos | Premium Oversized T-Shirts & Streetwear India",
        description: "Shop premium oversized T-shirts, anime tees, graphic tees, and streetwear styles for Indian youth at Cynos.",
        image: HERO_IMG,
      },
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "Cynos",
        url: window.location.origin,
        potentialAction: {
          "@type": "SearchAction",
          target: `${window.location.origin}/shop?search={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
      },
      removeJsonLdIds: ["product-json-ld"],
    });
  }, []);

  const trendingProducts = useMemo(() => {
    const tagged = homeProducts.trending.filter((product) => (
      product.badges || []
    ).some((badge) => /trend|best|limited|new/i.test(badge)));
    return tagged.length ? tagged : homeProducts.trending;
  }, [homeProducts.trending]);

  return (
    <main data-testid="home-page" className="pt-[66px] md:pt-[70px] lg:pt-[90px]">
      <OfferMarquee />
      <HeroSection image={HERO_IMG} />
      <TrustBadges />
      <ProductRailSection
        eyebrow="Trending Now"
        title="Fits in rotation."
        description="Fast-moving Cynos drops picked for oversized silhouettes, graphic energy, and everyday wear."
        products={trendingProducts}
        to="/shop?sort=best_selling"
        cta="Shop Trending"
        testId="trending-section"
        loading={loadingProducts}
      />
      <ProductRailSection
        eyebrow="Limited Drop / Best Seller"
        title="Most wanted."
        description="The tees customers keep adding first: premium cotton, clean fit, and checkout-ready sizes."
        products={homeProducts.bestSellers}
        to="/shop?sort=best_selling"
        cta="View All"
        testId="bestsellers-section"
        loading={loadingProducts}
        limit={10}
        alwaysShow
        emptyMessage="Most wanted drops will appear here when products load."
      />
      <CategorySection images={CATEGORY_IMAGES} />
      <ProductRailSection
        eyebrow="New Arrivals"
        title="Fresh drops."
        description="Newest prints and colors, built for quick browsing before the size run moves."
        products={homeProducts.newArrivals}
        to="/shop?sort=newest"
        cta="Shop New"
        testId="new-arrivals-section"
        loading={loadingProducts}
      />
      <BrandStorySection image={CATEGORY_IMAGES.oversized} />
      <CustomDesignSection image={CATEGORY_IMAGES.graphic} />
      <PromoBanner />
      <TestimonialSection reviews={REVIEWS} />
      <InstagramGallery images={GALLERY_IMGS} />
    </main>
  );
};

export default HomePage;
